import type { Rsd } from '../core/money.ts';

/** Ответы API. Поля повторяют схемы `GET /openapi.json`; суммы приходят decimal-строкой. */

export type OtpChannel = 'whatsapp' | 'viber' | 'telegram' | 'sms';

/** Мессенджер, в который ушёл код входа; `null` — код пришёл SMS. */
export function messengerName(channel: OtpChannel): string | null {
  return channel === 'sms' ? null : { whatsapp: 'WhatsApp', viber: 'Viber', telegram: 'Telegram' }[channel];
}

export type OtpRequested = {
  phone: string;
  expiresAt: string;
  /** Через сколько секунд сервер разрешит следующий код; кнопка повтора отсчитывает их. */
  resendAfterSeconds?: number;
  /** После мессенджера экран кода предлагает получить его по SMS. */
  channel: OtpChannel;
  /** Мок SMS отдаёт код прямо в ответе; на проде поля нет. */
  debugCode?: string;
};

export type SignedIn = { token: string; expiresAt: string; role: string; registered: boolean };

export type Profile = {
  id: string;
  /** Маскированный; пуст у аккаунта, созданного входом через Apple. */
  phone: string | null;
  email: string | null;
  /** Последний телефон для курьера, названный при оформлении. */
  contactPhone: string | null;
  role: string;
  name: string | null;
  avatarUrl: string | null;
  /** Дата рождения `YYYY-MM-DD` для товаров 18+; пусто — не спрашивали. */
  birthDate: string | null;
};

export type MyAddress = {
  id: string;
  text: string;
  lat: number;
  lng: number;
  comment: string | null;
  entrance: string | null;
  floor: string | null;
  apartment: string | null;
  intercom: string | null;
  /** false: адрес сохранён, но заказать на него нельзя — зона не обслуживается. */
  deliverable: boolean;
};

export type NewAddress = {
  text: string;
  lat: number;
  lng: number;
  comment?: string | undefined;
  entrance?: string | undefined;
  floor?: string | undefined;
  apartment?: string | undefined;
  intercom?: string | undefined;
};

export type AddressCreated = { id: string; outOfZone: boolean };

export type DeliveryTariff = {
  feeRsd: Rsd;
  /** Ноль: бесплатной доставки по сумме нет. */
  freeFromRsd: Rsd;
  /** Ноль: минимальная сумма заказа не проверяется. */
  minOrderRsd: Rsd;
  /** Лимит веса заказа в граммах; null — без лимита. */
  maxOrderWeightG: number | null;
};

export type DeliveryQuote = {
  covered: boolean;
  tariff: DeliveryTariff | null;
  /** Обещанное время доставки в эту точку: сборка плюс путь курьера до двери. */
  etaMinutes: number | null;
};

/** Контур зоны доставки для карты адреса: GeoJSON MultiPolygon, [polygon][ring][lng, lat]. */
export type DeliveryZone = {
  id: string;
  warehouseId: string;
  polygon: { coordinates: number[][][][] };
};

export type StoreStatus = {
  warehouseId: string | null;
  isOpen: boolean;
  /** Почему закрыто: `closed_manually`, `paused` или `hours`. */
  closedReason: string | null;
  opensAt: string;
  closesAt: string;
  closedManually: boolean;
  /** Наплыв: `delayed` — ETA уже продлён на `demandExtraMinutes`, `paused` — приём остановлен. */
  demandMode: 'normal' | 'delayed' | 'paused';
  demandExtraMinutes: number;
  etaMinMinutes: number;
  etaMaxMinutes: number;
  /** Продаются ли сейчас товары 18+ и в какие часы; без окна — всегда. */
  alcoholSaleOpen: boolean;
  alcoholSaleFrom: string | null;
  alcoholSaleUntil: string | null;
  /** Предлагать ли «оставить у двери» при оформлении. */
  leaveAtDoorEnabled: boolean;
  timezone: string;
};

export type Category = {
  id: string;
  name: string;
  parentId: string | null;
  sortOrder: number;
  imageUrl: string | null;
};

/** Наличие и цена товара на складе адреса: ответ `POST /v1/catalog/availability`. */
export type AvailabilityItem = {
  productId: string;
  isActive: boolean;
  availableQty: number;
  priceRsd: Rsd;
};

/** Пищевая ценность на 100 г или 100 мл; пустые поля карточка не показывает. */
export type Nutrition = {
  energyKcal: number | null;
  proteinG: number | null;
  fatG: number | null;
  carbsG: number | null;
};

export type DietTags = { vegan: boolean; glutenFree: boolean; sugarFree: boolean };

export type StorefrontItem = {
  id: string;
  name: string;
  categoryId: string | null;
  categoryName: string | null;
  unit: string;
  /** Фасовка с этикетки: «450 g», «1 l». */
  sizeLabel: string | null;
  /** Вес нетто в граммах (миллилитры для жидкостей). */
  netWeightG: number | null;
  /** Брутто единицы с упаковкой: корзина считает по нему вес заказа против лимита. */
  weightG: number | null;
  priceRsd: Rsd;
  /** Цена до скидки; пусто — скидки нет. */
  compareAtPriceRsd: Rsd | null;
  /** Последний день акции, `YYYY-MM-DD`. */
  promoUntil: string | null;
  vatRateBp: number;
  photoUrl: string | null;
  /** Короткое видео для карточки; пусто — карточка показывает фото. */
  videoUrl: string | null;
  /** Галерея карточки: главное фото первым, дальше дополнительные по порядку. */
  photos?: string[] | null;
  availableQty: number;
  ean: string | null;
  description: string | null;
  composition: string | null;
  countryOfOrigin: string | null;
  allergens: string | null;
  additives: string | null;
  shelfLifeDays?: number | null;
  storage?: string | null;
  isNew: boolean;
  /** Товар 18+: покупатель подтверждает возраст, заказ с ним не оставляют у двери. */
  ageRestricted: boolean;
  nutrition: Nutrition;
  tags: DietTags;
};

export type HomeBanner = {
  id: string;
  title: string;
  subtitle: string | null;
  imageUrl: string | null;
  color: string | null;
  collectionId: string | null;
  categoryId: string | null;
};

export type CollectionView = {
  id: string;
  title: string;
  subtitle: string | null;
  color: string | null;
  imageUrl: string | null;
  items: StorefrontItem[];
};

/** Главная одним ответом: статус магазина, баннеры, подборки и автоматические ленты. */
export type HomeFeed = {
  store: StoreStatus;
  banners: HomeBanner[];
  collections: CollectionView[];
  deals: StorefrontItem[];
  newItems: StorefrontItem[];
  mostOrdered: StorefrontItem[];
};

export type RelatedProducts = { oftenBoughtWith: StorefrontItem[]; similar: StorefrontItem[] };

export type RecentOrderLine = {
  productId: string;
  name: string;
  qty: number;
  priceRsd: Rsd;
  photoUrl: string | null;
};

/** Прошлый заказ для блока «Заказать снова». */
export type RecentOrder = {
  id: string;
  number: string;
  status: string;
  placedAt: string;
  itemsCount: number;
  totalRsd: Rsd;
  items: RecentOrderLine[];
};

export type OrderCreated = { id: string; number: string; totalRsd: Rsd; reservedUntil: string };

export type OrderListItem = {
  id: string;
  number: string;
  status: string;
  placedAt: string;
  addressText: string;
  itemsCount: number;
  totalRsd: Rsd;
  courierName: string | null;
  courierAvatarUrl: string | null;
  /** Обещанное время прибытия; null у заказов, оформленных до появления поля. */
  expectedAt: string | null;
};

export type OrderItem = {
  productId: string;
  /** Сербское название; переводы в `nameEn` и `nameRu`. */
  name: string;
  nameEn: string | null;
  nameRu: string | null;
  qty: number;
  pickedQty: number | null;
  /** Сколько единиц вернули после вручения. */
  returnedQty: number | null;
  priceRsd: Rsd;
  /** Скидка строки: доля промокода, бесплатные единицы или весь подарок. */
  discountRsd: Rsd;
  vatRateBp: number;
  photoUrl: string | null;
  unit: string | null;
  netWeightG: number | null;
  nutrition: Nutrition | null;
};

export type OrderPromotion = {
  promotionId: string;
  kind: string;
  title: string;
  titleEn: string | null;
  titleRu: string | null;
  discountRsd: Rsd;
};

/** Скидки корзины до оформления: что сервер применит к заказу с этими товарами и кодом. */
export type CartQuote = {
  itemsTotalRsd: Rsd;
  discountRsd: Rsd;
  /** Бонусы: сколько уйдёт на этот заказ и сколько на счёте. */
  creditUsedRsd: Rsd;
  creditBalanceRsd: Rsd;
  promoCode: string | null;
  applied: OrderPromotion[];
  gifts: { productId: string; name: string; qty: number }[];
};

export type SupportMessage = {
  id: number;
  senderRole: string;
  senderName: string | null;
  text: string;
  createdAt: string;
};

export type SupportThread = {
  id: string;
  orderId: string | null;
  orderNumber: string | null;
  status: string;
  clientUnread: boolean;
};

/** Переписка с поддержкой по заказу или общая; `thread` пуст до первого сообщения. */
export type SupportView = {
  thread: SupportThread | null;
  messages: SupportMessage[];
  hasUnread?: boolean | null;
};

export type CreditsView = {
  balanceRsd: Rsd;
  items: { id: number; amountRsd: Rsd; reason: string | null; orderNumber: string | null; createdAt: string }[];
};

export type FiscalReceipt = { receiptNumber: string | null; url: string | null; status: string };

export type OrderEvent = { from: string | null; to: string; actor: string; reason: string | null; at: string };

export type OrderRating = { stars: number; tipRsd: Rsd; comment: string | null };

/** Курьер везёт несколько заказов одним рейсом: остановка этого заказа, размер рейса, сколько до него. */
export type OrderRunPlace = { id: string; stop: number; size: number; ahead: number };

export type Order = {
  id: string;
  number: string;
  status: string;
  placedAt: string;
  warehouseName: string;
  addressText: string;
  addressLat: number;
  addressLng: number;
  addressComment: string | null;
  clientComment: string | null;
  leaveAtDoor: boolean;
  /** Спросит ли курьер код вручения: настройка сети, никогда при «оставить у двери». */
  requiresDeliveryCode: boolean;
  /** В заказе товар 18+: курьер проверяет документ при вручении. */
  ageRestricted: boolean;
  courierName: string | null;
  courierAvatarUrl: string | null;
  run: OrderRunPlace | null;
  itemsTotalRsd: Rsd;
  discountRsd: Rsd;
  creditUsedRsd: Rsd;
  promoCode: string | null;
  promotions: OrderPromotion[];
  deliveryFeeRsd: Rsd;
  totalRsd: Rsd;
  reservedUntil: string | null;
  expectedAt: string | null;
  deliveryCode: string | null;
  items: OrderItem[];
  receipt: FiscalReceipt | null;
  /** Карта, которой оплачен заказ. */
  paidWith: { last4: string | null; brand: string | null; issuer: string | null } | null;
  /** Сколько всего вернули на карту: недосбор возвращает разницу. */
  refundedRsd: Rsd | null;
  rating: OrderRating | null;
  history: OrderEvent[];
};

/** Позиция курьера по заказу в доставке; до выхода со склада и после вручения пуста. */
export type OrderTracking = {
  courierName: string | null;
  courierAvatarUrl: string | null;
  position: { lat: number; lng: number; accuracyM: number | null; at: string } | null;
};

export type PaymentResult = {
  /** `redirect` — карта вводится на странице эквайера по `paymentUrl`; `card_entry` — форма приложения шлёт карту на `postUrl`. */
  status: 'paid' | 'requires_3ds' | 'declined' | 'redirect' | 'card_entry';
  threeDsUrl?: string | null;
  paymentUrl?: string | null;
  postUrl?: string | null;
  reason?: string | null;
  receiptNumber?: string | null;
};

/** Карта, сохранённая у эквайера: маска, бренд и банк-эмитент, как его называет эквайер. */
export type SavedCard = {
  id: string;
  last4: string | null;
  brand: string | null;
  issuer: string | null;
  expMonth: number | null;
  expYear: number | null;
  isDefault: boolean;
};

/** Результат оплаты на странице эквайера: `pending`, пока эквайер не ответил. */
export type PaymentConfirmation = { status: 'paid' | 'declined' | 'pending'; reason?: string | null; receiptNumber?: string | null };

/** Как платит клиент: карта в приложении и сохранённые карты (`inline`) или страница эквайера (`hosted`); токен карты, Apple Pay. */
export type PaymentConfig = { provider: string; hosted: boolean; cardEntry: 'inline' | 'hosted'; savedCards: boolean; cardToken: boolean; applePay: boolean; testCard: boolean };

export type SupplementQuote = { chargeRsd: Rsd; totalBeforeRsd: Rsd; totalAfterRsd: Rsd };

export type SupplementResult = {
  status: 'paid' | 'declined';
  chargeRsd?: Rsd | null;
  totalRsd?: Rsd | null;
  reason?: string | null;
};

export type CancelResult = { status: string; moneyReturned: string; receiptVoided: boolean };

export type NotificationItem = {
  id: string;
  /** `order`, `promo`, `support`, `credit`, `redelivery`, `short_pick`, `return_refund` или `zone_open`. */
  kind: string;
  title: string | null;
  body: string | null;
  /** Сумма бонуса строки `credit`, возврат на карту строк `short_pick` и `return_refund`. */
  amountRsd: Rsd | null;
  orderId: string | null;
  orderNumber: string | null;
  orderStatus: string | null;
  productId: string | null;
  /** Адрес у `zone_open`; null у точки с карты. */
  addressText: string | null;
  readAt: string | null;
  createdAt: string;
};

export type NotificationsPage = { items: NotificationItem[]; unread: number };

export type NotificationSettings = { orders: boolean; promo: boolean };

/** Ответ на оценку: чаевые списаны (`paid`), не заданы (`none`) или карта отказала (`declined`). */
export type RatingResult = { tip: { status: string; reason: string | null } | null };

export type OrderItemInput = { productId: string; qty: number };

/** Остаток товара на складе адреса из ленты событий (`event: stock`). */
export type StockEvent = { productId: string; warehouseId: string; availableQty: number };

/** Позиция курьера по заказу клиента из ленты событий (`event: location`). */
export type LocationEvent = { orderId: string; lat: number; lng: number; at: string };

/** `event: notification` ленты: по виду `delivery_code` поверх экрана поднимается код вручения. */
export type NotificationEvent = { id: string; kind: string; orderId: string | null };

/** Привязка карты из профиля: сессия эквайера, куда форма шлёт номер карты. */
export type CardVerificationStart = { status: 'card_entry'; verificationId: string; postUrl: string };

/** Результат привязки: `pending`, пока эквайер не ответил. */
export type CardVerificationResult = { status: 'saved' | 'declined' | 'pending'; card?: SavedCard | null; reason?: string };
